import React, { useEffect } from 'react';
import classNames from 'classnames';
import Input from 'src/components/ui/Input/Input';
import { validateEmail } from 'src/utils/general';
import { useTranslation } from 'react-i18next';
import { Partner } from 'src/services/partner';
import styles from './AddressForm.module.scss';
import { OverlaySectionHeadline } from '../../../components/layouts/OverlaySectionHeadline/OverlaySectionHeadline';
import CountrySelect from '../../../components/ui/CountrySelect/CountrySelect';
import Icon from '../../../components/ui/Icon/Icon';

export interface AddressFormProps {
  partner?: Partner | null;
  editable?: boolean;
  headline?: string;
  withTaxId?: boolean;
  onChange?: (p: Partner | null, isValid: boolean) => void;
  errors?: any;
}

const AddressForm: React.FC<AddressFormProps> = ({
  partner,
  headline,
  onChange,
  withTaxId = true,
  editable = false,
  errors,
}) => {
  const { t } = useTranslation();
  const [partnerItem, setPartnerItem] = React.useState<Partner | null>(partner ?? null);

  useEffect(() => {
    setPartnerItem(partner ?? null);
  }, [partner]);

  const emptyPartner: Partner = {
    name: null,
    company_name: '',
    tax_id: '',
    email: '',
    phone: '',
    address: {
      street: '',
      street2: '',
      zip_code: '',
      city: '',
      country_name: '',
      country_code: '',
    },
    chat: null,
    active: true,
  };

  const isPartnerEmpty = (p: Partner | null) => {
    if (!p) return true;
    return (
      !p.company_name?.trim() &&
      !p.email?.trim() &&
      !p.phone?.trim() &&
      !p.tax_id?.trim() &&
      !p.address?.street?.trim() &&
      !p.address?.street2?.trim() &&
      !p.address?.zip_code?.trim() &&
      !p.address?.city?.trim() &&
      !p.address?.country_name?.trim() &&
      !p.address?.country_code?.trim()
    );
  };

  const requiredFieldsFilled = (p: Partner | null): boolean => {
    if (!p) return false;

    return (
      !!p.company_name?.trim() &&
      !!p.email?.trim() &&
      !!p.address?.city?.trim() &&
      !!p.address?.street?.trim() &&
      !!p.address?.zip_code?.trim() &&
      !!p.address?.country_code?.trim()
    );
  };

  const anyRequiredHasValue = (p: Partner | null): boolean => {
    if (!p) return false;
    return (
      !!p.company_name?.trim() ||
      !!p.email?.trim() ||
      !!p.address?.city?.trim() ||
      !!p.address?.street?.trim() ||
      !!p.address?.zip_code?.trim() ||
      !!p.address?.country_code?.trim() ||
      !!p.address?.country_name?.trim()
    );
  };

  const isPartial = (p: Partner | null) => {
    return anyRequiredHasValue(p) && !requiredFieldsFilled(p);
  };

  const updatePartnerAndNotify = (newPartner: Partner) => {
    const deepEmpty = isPartnerEmpty(newPartner);
    const filled = requiredFieldsFilled(newPartner);

    if (deepEmpty) {
      setPartnerItem(null);
      onChange?.(null, true);
      return;
    }
    setPartnerItem(newPartner);

    if (filled) {
      onChange?.(newPartner, true);
    } else {
      onChange?.(newPartner, false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const base = partnerItem ?? emptyPartner;
    const newPartner = { ...base, [key]: e.target.value };
    updatePartnerAndNotify(newPartner);
  };

  const handleInputChangeForAdress = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const base = partnerItem ?? emptyPartner;
    const newPartner = {
      ...base,
      address: {
        ...base.address,
        [key]: e.target.value,
      },
    };
    updatePartnerAndNotify(newPartner);
  };

  // Country needs country_code and country_name set together; doing it in one
  // update keeps them consistent and avoids the second write clobbering the
  // first (which made the select snap back to the previous country).
  const handleCountryChange = (country: { country_code: string; country_name: string }) => {
    const base = partnerItem ?? emptyPartner;
    const newPartner = {
      ...base,
      address: {
        ...base.address,
        ...country,
      },
    };
    updatePartnerAndNotify(newPartner);
  };

  const readFromPartnerItem = (path: string) => {
    if (!partnerItem) return undefined;
    return path.split('.').reduce((acc: any, key: string) => (acc ? acc[key] : undefined), partnerItem);
  };

  const safeError = (path: string) => {
    const obj = path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), errors);
    if (obj) return true;

    const requiredFieldPaths = [
      'partner.company_name',
      'partner.email',
      'partner.address.street',
      'partner.address.city',
      'partner.address.zip_code',
      'partner.address.country_code',
    ];

    const isReq = requiredFieldPaths.includes(path);
    if (!isReq) return false;

    if (isPartial(partnerItem)) {
      const val = readFromPartnerItem(path.replace(/^partner\./, ''));
      return val === null || val === undefined || (typeof val === 'string' && val.trim() === '');
    }

    return false;
  };

  const safeErrorMessage = (path: string) => {
    const obj = path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), errors);
    if (obj?.message) return obj.message;
    if (path === 'partner.email') {
      const currentEmail = (partnerItem ? partnerItem.email ?? '' : '') as string;
      if (currentEmail && !validateEmail(currentEmail)) {
        return t('errors.invalid_email_error');
      }
    }

    const requiredFieldPaths = [
      'partner.company_name',
      'partner.email',
      'partner.address.street',
      'partner.address.city',
      'partner.address.zip_code',
      'partner.address.country_code',
    ];
    if (requiredFieldPaths.includes(path) && isPartial(partnerItem)) {
      const val = readFromPartnerItem(path.replace(/^partner\./, ''));
      if (val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
        return t('errors.field_required_error');
      }
    }

    return undefined;
  };

  const val = (k: string) => (partnerItem ? (partnerItem as any)[k] ?? '' : '');
  const addr = (k: string) => (partnerItem?.address ? (partnerItem.address as any)[k] ?? '' : '');

  return (
    <>
      {headline && <OverlaySectionHeadline>{headline}</OverlaySectionHeadline>}
      <div className={styles.info}>
        <span className="typo-text-14--bold text-faded">{t('general.address')}</span>
        <div className={classNames('grid grid-cols-2 gap-16 w-full')}>
          <Input
            label={editable ? t('general.company_name') : undefined}
            placeholder=""
            value={val('company_name')}
            disabled={!editable}
            onChange={e => handleInputChange(e, 'company_name')}
            className="col-span-2"
            error={safeError('partner.company_name')}
            errorMessage={safeErrorMessage('partner.company_name') ?? ''}
          />

          <Input
            label={editable ? t('general.street') : undefined}
            placeholder=""
            value={addr('street')}
            disabled={!editable}
            onChange={e => handleInputChangeForAdress(e, 'street')}
            className="col-span-2"
            error={safeError('partner.address.street')}
            errorMessage={safeErrorMessage('partner.address.street') ?? ''}
          />

          <Input
            label={editable ? t('general.street2') : undefined}
            placeholder=""
            value={addr('street2')}
            disabled={!editable}
            onChange={e => handleInputChangeForAdress(e, 'street2')}
            className="col-span-2"
          />

          <Input
            label={editable ? t('general.zip_code') : undefined}
            placeholder={t('registration.placeholders.zip_code')}
            value={addr('zip_code')}
            disabled={!editable}
            onChange={e => handleInputChangeForAdress(e, 'zip_code')}
            className="col-span-1"
            error={safeError('partner.address.zip_code')}
            errorMessage={safeErrorMessage('partner.address.zip_code') ?? ''}
          />

          <Input
            label={editable ? t('general.city') : undefined}
            placeholder=""
            value={addr('city')}
            disabled={!editable}
            onChange={e => handleInputChangeForAdress(e, 'city')}
            className="col-span-1"
            error={safeError('partner.address.city')}
            errorMessage={safeErrorMessage('partner.address.city') ?? ''}
          />

          {withTaxId && (
            <Input
              label={editable ? t('general.tax_id') : undefined}
              placeholder=""
              value={val('tax_id')}
              disabled={!editable}
              onChange={e => handleInputChange(e, 'tax_id')}
              className="col-span-1"
            />
          )}

          <CountrySelect
            parent={partnerItem ?? ({} as Partner)}
            editable={editable}
            label={editable ? t('general.country') : undefined}
            className={withTaxId ? 'col-span-1' : 'col-span-2'}
            isControlled={false}
            onCountryChange={handleCountryChange}
          />
          {isPartial(partnerItem) && safeError('partner.address.country_code') && (
            <>
              <div className={`${withTaxId ? 'col-span-1' : ''}`} />
              <div className={`${withTaxId ? 'col-span-1' : 'col-span-2'} flex items-center gap-4 status-error-full`}>
                <Icon name="error" width={16} height={16} />
                <span className="typo-text-12--bold status-error-full">
                  {safeErrorMessage('partner.address.country_code')}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col w-full gap-16 mt-16">
        <div className={classNames(styles.info)}>
          <span className="typo-text-14--bold text-faded">{t('general.email')}</span>
          <Input
            placeholder={t('registration.placeholders.email')}
            disabled={!editable}
            value={val('email')}
            onChange={e => handleInputChange(e, 'email')}
            error={(!!val('email') && !validateEmail(val('email'))) || safeError('partner.email')}
            errorMessage={safeErrorMessage('partner.email') ?? ''}
          />
        </div>

        <div className={classNames(styles.info, 'items-center')}>
          <span className="typo-text-14--bold text-faded">{t('general.phone')}</span>

          <Input
            placeholder={t('registration.placeholders.phone')}
            value={val('phone')}
            disabled={!editable}
            onChange={e => handleInputChange(e, 'phone')}
          />
        </div>
      </div>
    </>
  );
};

export default AddressForm;
